import React, { useEffect, useMemo, useState } from "react";
import "./Login.css";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    loginEmployee,
    requestPasswordCode,
    changePassword,
    clearAuthFeedback,
} from "../../redux/authSlice";

export default function Login() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        isAuthenticated,
        statusLogin,
        statusPasswordCode,
        statusChangePassword,
        message,
        error,
    } = useSelector((state) => state.auth);

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);

    const [showChangeModal, setShowChangeModal] = useState(false);
    const [verificationCode, setVerificationCode] = useState("");
    const [newPwd, setNewPwd] = useState("");
    const [confirmPwd, setConfirmPwd] = useState("");
    const [showCode, setShowCode] = useState(false);
    const [showNewPwd, setShowNewPwd] = useState(false);
    const [showConfirmPwd, setShowConfirmPwd] = useState(false);
    const [modalEmail, setModalEmail] = useState("");
    // Errores de validación local; se muestran junto a los mensajes de la API.
    const [formError, setFormError] = useState("");
    const [modalError, setModalError] = useState("");

    const corporateEmailRegex = useMemo(
        () => /^[a-zA-Z0-9._%+-]+@asharastudios\.com$/,
        []
    );

    useEffect(() => {
        if (isAuthenticated) {
            navigate("/Profile", { replace: true });
        }
    }, [isAuthenticated, navigate]);

    useEffect(() => {
        return () => {
            dispatch(clearAuthFeedback());
        };
    }, [dispatch]);

    const isCorporateEmail = (value) => corporateEmailRegex.test(value.trim().toLowerCase());

    const handleLogin = async (e) => {
        e.preventDefault();
        setFormError("");

        if (!isCorporateEmail(email)) {
            setFormError("El correo debe terminar en @asharastudios.com");
            return;
        }

        const resultAction = await dispatch(
            loginEmployee({
                email: email.trim().toLowerCase(),
                password,
            })
        );

        if (loginEmployee.fulfilled.match(resultAction)) {
            navigate("/Profile", { replace: true });
        }
    };

    const handleRequestCode = async () => {
        setModalError("");
        if (!isCorporateEmail(modalEmail)) {
            setModalError("El correo debe terminar en @asharastudios.com");
            return;
        }

        await dispatch(requestPasswordCode({ email: modalEmail.trim().toLowerCase() }));
    };

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setModalError("");

        if (!isCorporateEmail(modalEmail)) {
            setModalError("El correo debe terminar en @asharastudios.com");
            return;
        }

        if (newPwd !== confirmPwd) {
            setModalError("Las contrasenas no coinciden");
            return;
        }

        if (!verificationCode.trim()) {
            setModalError("Ingresa el codigo de verificacion");
            return;
        }

        const resultAction = await dispatch(
            changePassword({
                email: modalEmail.trim().toLowerCase(),
                verificationCode: verificationCode.trim(),
                newPassword: newPwd,
            })
        );

        if (changePassword.fulfilled.match(resultAction)) {
            closeModal();
        }
    };

    const openModal = () => {
        setShowChangeModal(true);
        setModalEmail(email);
        setModalError("");
        dispatch(clearAuthFeedback());
    };

    const closeModal = () => {
        setShowChangeModal(false);
        setVerificationCode("");
        setNewPwd("");
        setConfirmPwd("");
        setModalEmail("");
        setModalError("");
        dispatch(clearAuthFeedback());
    };

    return (
        <article className="container-auth">
            <div className="login-card">
                <div className="login-header">
                    <img className="login-logo" src={`${process.env.PUBLIC_URL}/img/LogoImageSmall.png`} alt="Ashara Studios" />
                    <h1 className="login-title">
                        Ashara Studios
                        <span className="login-subtitle">Portal Administrativo</span>
                        <hr />
                    </h1>
                </div>

                <form className="login-form" onSubmit={handleLogin}>
                    {(formError || message || error) && (
                        <p className={formError || error ? "auth-message error" : "auth-message success"}>
                            {formError || error || message}
                        </p>
                    )}

                    <div className="login-field">
                        <label htmlFor="email">Email corporativo</label>
                        <input
                            id="email"
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="empleado@asharastudios.com"
                            autoComplete="email"
                        />
                    </div>

                    <div className="login-field">
                        <label htmlFor="password">Contrasena</label>
                        <div className="login-pwd-wrap">
                            <input
                                id="password"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="********"
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                className="btn-toggle-pwd"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? "Ocultar contrasena" : "Ver contrasena"}
                            >
                                {showPassword ? "◉" : "◎"}
                            </button>
                        </div>
                    </div>

                    <button type="submit" className="login-btn" disabled={statusLogin === "loading"}>
                        {statusLogin === "loading" ? "Ingresando..." : "Ingresar"}
                    </button>

                    <button
                        type="button"
                        className="btn-link"
                        onClick={openModal}
                    >
                        Cambiar contrasena
                    </button>
                </form>
            </div>

            {showChangeModal && (
                <div
                    className="modal-overlay"
                    onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}
                >
                    <div className="modal-change-pwd">
                        <h2>
                            Cambiar Contrasena
                            <hr />
                        </h2>

                        {(modalError || message || error) && (
                            <p className={modalError || error ? "auth-message error" : "auth-message success"}>
                                {modalError || error || message}
                            </p>
                        )}

                        <form className="login-form" onSubmit={handleChangePassword}>
                            <div className="login-field">
                                <label>Email corporativo</label>
                                <input
                                    type="email"
                                    value={modalEmail}
                                    onChange={(e) => setModalEmail(e.target.value)}
                                    placeholder="empleado@asharastudios.com"
                                    autoComplete="email"
                                />
                            </div>

                            <div className="login-field">
                                <label>Codigo de verificacion</label>
                                <div className="login-pwd-wrap">
                                    <input
                                        type={showCode ? "text" : "password"}
                                        value={verificationCode}
                                        onChange={(e) => setVerificationCode(e.target.value)}
                                        placeholder="Codigo recibido por correo"
                                    />
                                    <button type="button" className="btn-toggle-pwd" onClick={() => setShowCode(!showCode)}>
                                        {showCode ? "◉" : "◎"}
                                    </button>
                                </div>
                            </div>

                            <div className="login-field">
                                <label>Nueva contrasena</label>
                                <div className="login-pwd-wrap">
                                    <input
                                        type={showNewPwd ? "text" : "password"}
                                        value={newPwd}
                                        onChange={(e) => setNewPwd(e.target.value)}
                                        placeholder="********"
                                        autoComplete="new-password"
                                    />
                                    <button type="button" className="btn-toggle-pwd" onClick={() => setShowNewPwd(!showNewPwd)}>
                                        {showNewPwd ? "◉" : "◎"}
                                    </button>
                                </div>
                            </div>

                            <div className="login-field">
                                <label>Confirmar contrasena</label>
                                <div className="login-pwd-wrap">
                                    <input
                                        type={showConfirmPwd ? "text" : "password"}
                                        value={confirmPwd}
                                        onChange={(e) => setConfirmPwd(e.target.value)}
                                        placeholder="********"
                                        autoComplete="new-password"
                                    />
                                    <button type="button" className="btn-toggle-pwd" onClick={() => setShowConfirmPwd(!showConfirmPwd)}>
                                        {showConfirmPwd ? "◉" : "◎"}
                                    </button>
                                </div>
                            </div>

                            <div className="modal-actions">
                                <button
                                    type="button"
                                    className="secundary"
                                    onClick={handleRequestCode}
                                    disabled={statusPasswordCode === "loading"}
                                >
                                    {statusPasswordCode === "loading" ? "Enviando..." : "Enviar codigo"}
                                </button>
                                <button type="submit" disabled={statusChangePassword === "loading"}>
                                    {statusChangePassword === "loading" ? "Guardando..." : "Guardar"}
                                </button>
                            </div>

                            <button type="button" className="btn-link" onClick={closeModal}>Cancelar</button>
                        </form>
                    </div>
                </div>
            )}
        </article>
    );
}
